import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { defineMessages, FormattedMessage, FormattedRelativeTime, FormattedTime, useIntl } from 'react-intl';
import { clearLastReplay, retryAllOutbox, useAnyAsOf, useLastReplay, useOnline, useOutbox } from '../offline/client';

const m = defineMessages({
  offline: { id: 'offline.banner', defaultMessage: 'You are offline', description: 'Offline banner: lead text' },
  asOf: { id: 'offline.asOf', defaultMessage: 'showing what was read {when}', description: 'Offline banner: freshness of the cached data; when is a relative time such as "12 minutes ago"' },
  queued: { id: 'offline.queued', defaultMessage: '{count, plural, one {# change waiting to send} other {# changes waiting to send}}', description: 'Offline banner: outbox size' },
  retry: { id: 'offline.retry', defaultMessage: 'Retry', description: 'Offline banner button: try the network again' },
  stale: { id: 'offline.stale', defaultMessage: 'Some of this page is from {time}; the server did not answer.', description: 'Banner when online but a read came from the cache (server unreachable); time is a clock time' },
  sent: { id: 'offline.sent', defaultMessage: '{count, plural, one {Back online — your queued change was sent.} other {Back online — your # queued changes were sent.}}', description: 'Banner after the outbox replayed successfully' },
  failed: { id: 'offline.failed', defaultMessage: '{count, plural, one {# queued change could not be sent ({reason}); it stays on this device — open the decision to retry or discard it.} other {# queued changes could not be sent ({reason}); they stay on this device — open the decisions to retry or discard them.}}', description: 'Banner after the outbox gave up retrying an entry; reason is the server status' },
  refused: { id: 'offline.refused', defaultMessage: '{count, plural, one {# queued change was refused by the server: {reason}} other {# queued changes were refused by the server: {reason}}}', description: 'Banner after the outbox replayed with refusals; reason is the server message' },
  dismiss: { id: 'offline.dismiss', defaultMessage: 'Dismiss', description: 'Button closing the back-online banner' },
});

/**
 * The design-system offline banner (planning-app.md §4.2 "as of", §4.4; design-system/offline-banner.html).
 * Under the top bar while offline, with the freshness of what is on screen and the outbox size;
 * a quieter line when a read was served from the cache although the browser thinks it is online;
 * a short "back online, sent" confirmation after a replay. It is a status region: it stays, so it
 * is not announced as an alert.
 */
export function OfflineBanner() {
  const intl = useIntl();
  const qc = useQueryClient();
  const online = useOnline();
  const asOf = useAnyAsOf();
  const outbox = useOutbox();
  const replay = useLastReplay();
  const [busy, setBusy] = useState(false);

  // After a replay the lists behind the queued writes are refreshed and the banner shows the result for a while.
  useEffect(() => {
    if (replay.length === 0) return;
    void qc.invalidateQueries();
    const t = setTimeout(clearLastReplay, 8000);
    return () => clearTimeout(t);
  }, [replay, qc]);

  const retry = async () => {
    setBusy(true);
    try {
      await retryAllOutbox();
      await qc.invalidateQueries();
    } finally {
      setBusy(false);
    }
  };

  const sent = replay.filter((r) => r.outcome === 'sent').length;
  const refused = replay.filter((r) => r.outcome === 'refused');
  const failed = replay.filter((r) => r.outcome === 'failed');
  // A pass that only found the network still down has nothing to announce; the queue banner below says what waits.
  if (sent > 0 || refused.length > 0 || failed.length > 0) {
    return (
      <div className="offline-banner back" role="status" data-testid="offline-banner">
        <CloudIcon />
        <span>
          {sent > 0 && <FormattedMessage {...m.sent} values={{ count: sent }} />}
          {refused.length > 0 && <FormattedMessage {...m.refused} values={{ count: refused.length, reason: refused[0]!.outcome === 'refused' ? refused[0]!.message : '' }} />}
          {failed.length > 0 && <FormattedMessage {...m.failed} values={{ count: failed.length, reason: failed[0]!.outcome === 'failed' ? failed[0]!.message : '' }} />}
        </span>
        <span className="spacer" />
        <button type="button" className="btn sm" onClick={clearLastReplay}>
          <FormattedMessage {...m.dismiss} />
        </button>
      </div>
    );
  }

  if (online && !asOf && outbox.length === 0) return null;

  if (online && asOf) {
    return (
      <div className="offline-banner" role="status" data-testid="offline-banner">
        <CloudIcon />
        <span>
          <FormattedMessage {...m.stale} values={{ time: <FormattedTime value={new Date(asOf)} /> }} />
          {outbox.length > 0 && (
            <>
              {' · '}
              <FormattedMessage {...m.queued} values={{ count: outbox.length }} />
            </>
          )}
        </span>
        <span className="spacer" />
        <button type="button" className="btn sm" onClick={retry} disabled={busy}>
          <FormattedMessage {...m.retry} />
        </button>
      </div>
    );
  }

  if (online && outbox.length > 0) {
    return (
      <div className="offline-banner" role="status" data-testid="offline-banner">
        <CloudIcon />
        <span>
          <FormattedMessage {...m.queued} values={{ count: outbox.length }} />
        </span>
        <span className="spacer" />
        <button type="button" className="btn sm" onClick={retry} disabled={busy}>
          <FormattedMessage {...m.retry} />
        </button>
      </div>
    );
  }

  return (
    <div className="offline-banner" role="status" data-testid="offline-banner">
      <CloudIcon />
      <span>
        <b>
          <FormattedMessage {...m.offline} />
        </b>
        {asOf && (
          <>
            {' — '}
            <FormattedMessage {...m.asOf} values={{ when: <FormattedRelativeTime value={Math.round((new Date(asOf).getTime() - Date.now()) / 1000)} updateIntervalInSeconds={60} /> }} />
          </>
        )}
        {outbox.length > 0 && (
          <>
            {' · '}
            <FormattedMessage {...m.queued} values={{ count: outbox.length }} />
          </>
        )}
      </span>
      <span className="spacer" />
      <button type="button" className="btn sm" onClick={retry} disabled={busy} aria-label={intl.formatMessage(m.retry)}>
        <FormattedMessage {...m.retry} />
      </button>
    </div>
  );
}

function CloudIcon() {
  return (
    <svg className="ic" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 18a4 4 0 0 1-.6-7.95A6 6 0 0 1 18 8.5 3.5 3.5 0 0 1 17.5 15.5" />
      <path d="M3 3l18 18" />
    </svg>
  );
}

/** A small "as of" stamp for a card whose data came from the offline cache. */
export function AsOf({ at, label }: { at: string | null; label?: string }) {
  if (!at) return null;
  return (
    <span className={`asof stale`} data-testid="as-of">
      <svg className="ic" viewBox="0 0 24 24" aria-hidden="true" style={{ width: 14, height: 14 }}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
      {label ?? <FormattedMessage id="offline.asOfShort" defaultMessage="as of {time}" description="Card stamp: when the cached data was read; time is a clock time" values={{ time: <FormattedTime value={new Date(at)} /> }} />}
    </span>
  );
}
